// Settings card (office and admin, 18 October 2026): is the weekly database backup working?
//
// Read only, and derived live from the platform's record of each backup run (GET /api/backup/status). The weekly backup once
// ran broken for two weeks with nothing to say so, so this card never reads a missing answer as good news: no backup on
// record, a record that could not be read and an overdue backup each say exactly that. The status is always written in
// words as well as colour. Downloading a backup by hand stays in the admin-only Database backup section.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { BackupStatus } from "../../lib/api";
import { Loader } from "../../components/Loader";
import { StatusPill, type PillTone } from "../../components/StatusPill";
import { formatDateLong } from "../../lib/format";
import { useReportSettingsSummary } from "./settingsSummary";

export function backupStatusLabel(s: BackupStatus): { text: string; tone: PillTone } {
  switch (s.state) {
    case "fresh":
      return s.problemCount > 0 ? { text: "Last backup is recent, but a later run failed", tone: "amber" } : { text: "Backups are working", tone: "green" };
    case "stale":
      return { text: "Backup overdue", tone: "red" };
    case "interrupted":
      return { text: "Backup interrupted", tone: "red" };
    case "never_recorded":
      return { text: "No backup recorded", tone: "red" };
    default:
      return { text: "Could not check", tone: "red" };
  }
}

export function BackupStatusCard({ refreshKey }: { refreshKey?: number }) {
  const [view, setView] = useState<BackupStatus | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let live = true;
    setState("loading");
    Promise.resolve()
      .then(() => api.backupStatus.get())
      .then((v) => {
        if (!live) return;
        setView(v);
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  const label = view ? backupStatusLabel(view) : null;
  // The Settings section header shows this card's state (it reports to nothing anywhere else).
  useReportSettingsSummary(label ? `Database backup: ${label.text}` : null);
  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Database backup status">
      <h2 className="mb-2 font-heading text-lg text-ele-blue">Database backup status</h2>
      <p className="mb-3 text-sm text-ele-body">
        A full backup should be filed to Google Drive every Sunday. This shows whether that is happening. It is
        read from the platform&apos;s own record of each run.
      </p>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !view || !label ? (
        <p role="status" className="text-sm text-ele-strong">
          The backup status could not be read just now. That does not mean the backups are fine. Try again in a moment.
        </p>
      ) : (
        <div className="space-y-3">
          <StatusPill word={label.text} tone={label.tone} />
          <p className="text-sm text-ele-strong">{view.headline}</p>
          {view.interruptedRun && (
            <p className="text-sm text-ele-strong">
              <span className="text-ele-body">Run that did not finish: </span>
              started {formatDateLong(view.interruptedRun.startedAt)}
              {view.interruptedRun.filename ? `, making ${view.interruptedRun.filename}` : ""}. Nothing was recorded after the
              start, so the host most likely stopped it.
            </p>
          )}
          {view.lastSuccess && (
            <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
              <dt className="text-ele-body">Last good backup</dt>
              <dd className="text-ele-strong">
                {formatDateLong(view.lastSuccess.at)} ({view.lastSuccess.ageDays === 0 ? "today" : `${view.lastSuccess.ageDays} ${view.lastSuccess.ageDays === 1 ? "day" : "days"} ago`})
              </dd>
              {view.lastSuccess.filename && (
                <>
                  <dt className="text-ele-body">File</dt>
                  <dd className="text-ele-strong [overflow-wrap:anywhere]">{view.lastSuccess.filename}</dd>
                </>
              )}
            </dl>
          )}
          {view.problemCount > 0 && (
            <div>
              <h3 className="mb-1 font-menu text-xs uppercase tracking-wide text-ele-body">
                Problems since the last good backup ({view.problemCount})
              </h3>
              <ul className="space-y-1 text-sm text-ele-strong" aria-label="Problems since the last good backup">
                {view.problemsSinceLastSuccess.map((p) => (
                  <li key={`${p.at}-${p.kind}`} className="[overflow-wrap:anywhere]">
                    <span className="text-ele-body">{formatDateLong(p.at)}: </span>
                    {p.what}
                  </li>
                ))}
              </ul>
              {view.problemCount > view.problemsSinceLastSuccess.length && (
                <p className="mt-1 text-xs text-ele-body">Showing the {view.problemsSinceLastSuccess.length} most recent.</p>
              )}
            </div>
          )}
          <p className="text-sm text-ele-strong">
            <span className="text-ele-body">Independent off-platform copy: </span>
            {view.offPlatformCopy?.configured ? view.offPlatformCopy.note || "Configured." : view.offPlatformCopy?.note ?? "Not configured."}
          </p>
          <p className="text-xs text-ele-body">
            A backup that is overdue by more than {view.staleAfterDays} days raises a notice on the dashboard, and so does a run
            that starts and records no end within 30 minutes. Every run records its start, and a run the host stops records the
            stop, so a quiet record means no run started. Runs before 19 October 2026 recorded neither.
          </p>
        </div>
      )}
    </section>
  );
}
